import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetPhotography,
  useUpdatePhotography,
} from "@/api/photography/photography";
import {
  appendNoDuplicateRecordNumberRefine,
  photographySchema,
} from "@/api/photography/photography-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import PhotographyPrintSingleDrawer from "@/components/photography/PhotographyPrintSingleDrawer";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import i18n, { SupportedLocale } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldPhotographySummary } from "@/lib/computeFields";
import { Photography } from "@/lib/types";
import { extractHtmlText } from "@/lib/utils";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateRecordNumberRefine(
  photographySchema.passthrough(),
);

const EditPhotography = ({ photography }: { photography: Photography }) => {
  const form = useForm({
    resolver: standardSchemaResolver(schema),
    defaultValues: {
      ...photography,
    },
  });

  const { data: attachments } = useGetAttachmentsByDocId(photography.id);

  const photographyUpdater = useUpdatePhotography();

  const { t } = useTranslation();

  const { relativePosition } = useDashboardParams<Photography>({
    statusField: "photosPublicationStatus",
    resourceName: "photography",
    relativePosition: {
      currentId: photography.id,
      positionFetcher: getRelativePosition,
    },
  });

  const [, setSearchParams] = useSearchParams();

  const handleLegendeRedigee = (data: Photography) => {
    return data
      ? computeFieldPhotographySummary(data, {
          language: i18n.language as SupportedLocale,
        })
      : "";
  };

  const queryClient = useQueryClient();

  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values) =>
            photographyUpdater.mutate(
              {
                ...values,
                ...(status && { photosPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("photographySavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            ),
          (invalid) => {
            if (invalid.photosDescriptionNumeroEnregistrement != undefined)
              setSearchParams({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["photography", photography.id],
        });
        queryClient.invalidateQueries({ queryKey: ["photographies"] });

        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: photography.id }],
        });
      }}
      relativePosition={relativePosition}
      publicationStatus={photography.photosPublicationStatus}
      title={extractHtmlText(photography.photosDescriptionTitre)}
      type={"photography"}
      id={photography.id}
      reference={photography.photosDescriptionNumeroEnregistrement}
      picturesData={
        attachments && { pictures: attachments, type: "photography" }
      }
      form={form}
      print={<PhotographyPrintSingleDrawer selectedPhotography={photography} />}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={
          [
            {
              value: "description",
              label: t("photographyDescription"),
            },
            {
              value: "sources",
              label: t("sources"),
            },
            {
              value: "files",
              label: t("fichiers"),
            },
            {
              value: "links",
              label: t("linkedEntries"),
            },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row>
              <AF.Text
                name="photosDescriptionNumeroEnregistrement"
                className="w-64"
              />
              <AF.Checkbox name="photosDescriptionCopies" align={true} />
            </AF.Row>
            <ModifiableList
              name="photosDescriptionType"
              listKey="liste-types-document"
            />
            <AF.Row>
              <AF.Text name="photosDescriptionClassement" />
              <AF.Text name="photosDescriptionRangement" />
            </AF.Row>
            <ModifiableList
              name="photosDescriptionLocalisation"
              listKey="liste-bibliotheques-archives"
            />
            <AF.Text
              className="w-16"
              name="photosDescriptionNombreExemplairesArchives"
            />
            <AF.RichText name="photosDescriptionTitre" />
            <AF.Row>
              <AF.Text name="photosDescriptionDate" />
              <ModifiableList
                name="photosDescriptionTheme"
                listKey="liste-themes"
              />
            </AF.Row>
            <TwoDimensionalInput parentName="photosDescriptionDimensions" />
            <ModifiableList
              name="photosDescriptionTechnique"
              listKey="liste-techniques-document"
            />
            <AF.Text name="photosDescriptionLieuDeCreation" />
            <ModifiableList
              name="photosDescriptionPhotographe"
              listKey="liste-photographes"
            />
            <ModifiableList
              name="photosDescriptionCopyright"
              listKey="liste-credits"
            />
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="photosDescriptionLegendeRedigee"
              computeAction={handleLegendeRedigee}
            />
            <AF.RichText name="photosDescriptionNotes" />
            <AF.Row>
              <AF.Checkbox name="photosFichierPhotoBasseDefinitionUniquement" />
              <AF.Checkbox name="photosFichierPhotoScanAFaire" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="photosFichierPhotoOkWeb" />
              <AF.Checkbox name="photosFichierPhotoHdOk" />
            </AF.Row>
            <AF.RadioGroup
              items={[
                { value: "photosAcquisitionDocumentAAcquerir" },
                { value: "photosAcquisitionDocumentAcquis" },
              ]}
            />
            <AF.Row>
              <AF.Text name="photosAcquisitionDateAcquisition" />
              <AF.Text name="photosAcquisitionVendeur" />
            </AF.Row>
            <AF.Text name="photosAcquisitionNotes" />
            <AF.Text name="photosMouvementsDepot" />
            <AF.Text name="photosMouvementsNotesDepot" />
            <AF.LegacySelectCurrency name="photosMouvementsValeurAssurance" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={<AddFileModal id={photography.id} />}
        >
          <AF.OthersFilesTable name="diversFiles" docId={photography.id} />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={photography.id}
            type="photography"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "photographyInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "photographiesDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "photographiesDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "photographiesDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={photography}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditPhotographyPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetPhotography(id);

  return (
    <>
      {" "}
      {data && <EditPhotography key={data.id} photography={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">photographieNExistePasLabel</h2>
      )}
    </>
  );
};
